import { CurrencyCode } from '../../models/currency/currency-code.enum';
import { LocaleId } from '../../models/locale/locale-id.enum';

/**
 * Intl-based currency formatter that mirrors Angular's digitsInfo shape.
 * Examples:
 *  - formatCurrency(9, 'GBP', 'en-GB', '1.2-2') -> "£9"
 *  - formatCurrency('9.00', 'GBP', 'en-GB', '1.2-2') -> "£9"
 *  - formatCurrency(9.5, 'GBP', 'en-GB', '1.2-2') -> "£9.50"
 *  - formatCurrency(9.99, 'EUR', 'de-DE', '1.2-2') -> "9,99€"
 *  - formatCurrency(undefined, 'USD', 'en-US') -> ""
 */
export function formatCurrency(
  value: number | string | undefined | null,
  currencyCode: CurrencyCode,
  localeId: LocaleId,
  digitsInfo = '1.2-2',
): string {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  const num = Number(value);
  if (Number.isNaN(num)) {
    return '';
  }

  const { minimumIntegerDigits, minimumFractionDigits, maximumFractionDigits } =
    parseDigitsInfo(digitsInfo);

  // Ensure non-whole amounts display two decimal places (e.g., £9.50),
  // while whole amounts can be trimmed (e.g., £9)
  const isWholeAmount = Number.isInteger(num);
  const effectiveMinFractionDigits = isWholeAmount
    ? 0
    : Math.max(minimumFractionDigits, 2);
  const effectiveMaxFractionDigits = isWholeAmount
    ? 0
    : Math.max(maximumFractionDigits, 2);

  return new Intl.NumberFormat(localeId, {
    style: 'currency',
    currency: currencyCode,
    minimumIntegerDigits,
    minimumFractionDigits: effectiveMinFractionDigits,
    maximumFractionDigits: effectiveMaxFractionDigits,
    useGrouping: true,
    // currencyDisplay defaults to "symbol" which matches the old "wide" usage.
    // If you ever want the narrow symbol, set currencyDisplay: 'narrowSymbol'.
  }).format(num);
}

/**
 * Parses Angular-style digitsInfo: "minInt.minFrac-maxFrac"
 * Examples:
 *  - "1.2-2" -> minInt=1, minFrac=2, maxFrac=2
 *  - "2.0-3" -> minInt=2, minFrac=0, maxFrac=3
 *  - "1.0-0" -> minInt=1, minFrac=0, maxFrac=0
 */
export function parseDigitsInfo(digitsInfo: string): {
  minimumIntegerDigits: number;
  minimumFractionDigits: number;
  maximumFractionDigits: number;
} {
  // Defaults align with the provided default "1.2-2"
  let minimumIntegerDigits = 1;
  let minimumFractionDigits = 2;
  let maximumFractionDigits = 2;

  const m = /^(\d+)?(?:\.(\d+)(?:-(\d+))?)?$/.exec(digitsInfo.trim());
  if (m) {
    if (m[1] !== undefined) minimumIntegerDigits = parseInt(m[1], 10);
    if (m[2] !== undefined) minimumFractionDigits = parseInt(m[2], 10);
    if (m[3] !== undefined) {
      maximumFractionDigits = parseInt(m[3], 10);
    } else {
      maximumFractionDigits = minimumFractionDigits;
    }
  }

  return {
    minimumIntegerDigits,
    minimumFractionDigits,
    maximumFractionDigits,
  };
}
